import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Container,
  Paper,
  CircularProgress,
} from '@mui/material';
// import { useNavigate } from 'react-router-dom';
import Navbar from '../mainPage/components/Navbar';
import { submitContactForm } from '../../apis/contactApi';
import { toast } from 'react-toastify';

const ContactPage = () => {
  // const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    product: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const contactSection = document.getElementById('contact-section');
      if (contactSection) {
        const rect = contactSection.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
      }
    };

    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.addEventListener('mousemove', handleMouseMove);
      return () => contactSection.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Basic validation
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields', { 
        position: 'top-right', 
        autoClose: 3000 
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address', { 
        position: 'top-right', 
        autoClose: 3000 
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await submitContactForm(formData);
      
      // Success feedback
      toast.success('Thank you for your message! Our representative will call you within 48 hours.', { 
        position: 'top-right', 
        autoClose: 6000 
      });
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        product: '',
        message: '',
      });
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      
      // Error feedback
      toast.error('Failed to send message. Please try again or contact us directly.', { 
        position: 'top-right', 
        autoClose: 5000 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleClose = () => {
  //   navigate('/');
  // };

  return (
    <>
      <Navbar hideLoginButton={false} enableScrollEffect={true} />
      
      <Box
        id="contact-section"
        sx={{
          width: '100vw',
          Height: '100vh',
          background: '#3044A9',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          cursor: 'none',
          pt: { xs: 10, sm: 12 }, // Account for navbar height
          pb: 7,
        }}
      >
        {/* Enhanced Grid Pattern Background - Full Coverage */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
          `,
            backgroundSize: '80px 80px',
            opacity: 0.25,
          }}
        />

        {/* Cursor Following Grid Highlight */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
          `,
            backgroundSize: '80px 80px',
            maskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            WebkitMaskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
          }}
        />

        {/* Fine Grid Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
          `,
            backgroundSize: '20px 20px',
            opacity: 0.2,
          }}
        />

        {/* Cursor Following Fine Grid */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px)
          `,
            backgroundSize: '20px 20px',
            maskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            WebkitMaskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
          }}
        />

        {/* Cursor Glow Effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3,
            background: `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.06) 0%, transparent 70%)`,
            transition: 'background 0.2s ease-out',
            pointerEvents: 'none',
          }}
        />

        {/* Animated Grid Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)
          `,
            animation: 'gridPulse 8s ease-in-out infinite',
            '@keyframes gridPulse': {
              '0%, 100%': {
                opacity: 0.3,
              },
              '50%': {
                opacity: 0.6,
              },
            },
          }}
        />

        {/* Depth and Glow Effects */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            background: `
            radial-gradient(ellipse at center, rgba(255, 255, 255, 0.02) 0%, transparent 70%),
            linear-gradient(45deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
            linear-gradient(-45deg, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
          `,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '10%',
              left: '10%',
              right: '10%',
              bottom: '10%',
              background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
              borderRadius: '50%',
              filter: 'blur(40px)',
              animation: 'float 6s ease-in-out infinite',
            },
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) scale(1)',
                opacity: 0.3,
              },
              '50%': {
                transform: 'translateY(-10px) scale(1.05)',
                opacity: 0.5,
              },
            },
          }}
        />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
          <Paper
            elevation={0}
            sx={{
              bgcolor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              p: { xs: 3, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              minHeight: '500px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              my: 4, // Add vertical margin for better centering
            }}
          >
            {/* Header */}
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: '#0f172a', 
                mb: 0.5,
                textAlign: 'center'
              }}
            >
              Get In Touch
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#64748b', 
                mb: 4,
                textAlign: 'center'
              }}
            >
              Have a question? Drop it here, and our team will reach out to you.
            </Typography>

            {/* Form */}
            <Box 
              component="form" 
              onSubmit={handleSubmit}
              noValidate 
              autoComplete="off" 
              sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column' 
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
                Full Name*
              </Typography>
              <TextField
                fullWidth
                placeholder="eg: John Doe"
                size="small"
                variant="outlined"
                value={formData.fullName}
                onChange={handleInputChange('fullName')}
                required
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f8fafc',
                    borderRadius: '10px',
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: '#94a3b8', opacity: 1 },
                }}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
                Email*
              </Typography>
              <TextField
                fullWidth
                placeholder="eg: john@email.com"
                size="small"
                variant="outlined"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f8fafc',
                    borderRadius: '10px',
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: '#94a3b8', opacity: 1 },
                }}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
                What product are you interested in?
              </Typography>
              <TextField
                fullWidth
                placeholder="eg: ETL Data Pipeline"
                size="small"
                variant="outlined"
                value={formData.product}
                onChange={handleInputChange('product')}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f8fafc',
                    borderRadius: '10px',
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: '#94a3b8', opacity: 1 },
                }}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
                How can we help you?
              </Typography>
              <TextField
                fullWidth
                placeholder="Type your message here..."
                multiline
                minRows={4}
                variant="outlined"
                value={formData.message}
                onChange={handleInputChange('message')}
                sx={{
                  mb: 2,
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f8fafc',
                    borderRadius: '10px',
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: '#94a3b8', opacity: 1 },
                }}
              />

              <Typography variant="caption" sx={{ color: '#64748b', mb: 3 }}>
                By submitting this form you agree to our{' '}
                <Link href="#" underline="hover" sx={{ color: '#334155', fontWeight: 600 }}>
                  Privacy Policy
                </Link>
              </Typography>

              {/* Buttons at bottom */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 'auto' }}>
                {/* <Button
                  variant="outlined"
                  onClick={handleClose}
                  sx={{
                    borderColor: '#6268A6',
                    color: '#6268A6',
                    borderRadius: '10px',
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { 
                      borderColor: '#545b99', 
                      color: '#545b99',
                      backgroundColor: 'rgba(98, 104, 166, 0.04)'
                    },
                  }}
                >
                  Back to Home
                </Button> */}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    bgcolor: '#6268A6',
                    borderRadius: '10px',
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#545b99', boxShadow: 'none' },
                    '&:disabled': { 
                      bgcolor: '#94a3b8', 
                      color: '#ffffff' 
                    },
                  }}
                >
                  {isSubmitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                      Sending...
                    </Box>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default ContactPage;